import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import { FIGMA_COLORS } from "components-redesign/styles";
import { COLORS_TO_BE_REVIEWED } from "components-redesign/styles/colors";
import {
  TypographyBodyMedium500,
  TypographyBodySmall,
} from "components-redesign/Typography/Typography";
import { makeStyles } from "components/mui-core";
import React from "react";

const COPIED_FEEDBACK_MS = 1500;

const useStyles = makeStyles()({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    width: "100%",
    padding: 12,
    borderRadius: 12,
    border: `1px dashed ${FIGMA_COLORS.outlineLight}`,
    backgroundColor: COLORS_TO_BE_REVIEWED.white,
    color: FIGMA_COLORS.onSurfaceLight,
    textAlign: "left",
    cursor: "pointer",
    boxSizing: "border-box",
    font: "inherit",
    transition: "background-color 150ms linear, border-color 150ms linear",
    "&:hover": {
      backgroundColor: COLORS_TO_BE_REVIEWED.primary95,
      borderStyle: "solid",
      borderColor: FIGMA_COLORS.primaryLight,
      color: FIGMA_COLORS.primaryLight,
    },
  },
  // Wins over :hover - the pointer is still inside the box right after a copy.
  copied: {
    "&, &:hover": {
      // the confirmation green at 10%, which is how the design tints it
      backgroundColor: `${COLORS_TO_BE_REVIEWED.success}1A`,
      borderStyle: "solid",
      borderColor: COLORS_TO_BE_REVIEWED.success,
      color: COLORS_TO_BE_REVIEWED.success,
    },
  },
  value: {
    color: COLORS_TO_BE_REVIEWED.black,
    overflowWrap: "anywhere",
  },
  action: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 4,
  },
});

type IProps = {
  value: string;
  copyLabel?: string;
  copiedLabel?: string;
  "data-qa"?: string;
};

/** A value the user is meant to copy out, e.g. an MFA setup key. */
function CopyField({
  value,
  copyLabel = "Copy",
  copiedLabel = "Copied",
  "data-qa": dataQa = "copy-field",
}: IProps) {
  const { classes, cx } = useStyles();
  const [isCopied, setIsCopied] = React.useState(false);

  React.useEffect(() => {
    if (!isCopied) return;
    const timeoutId = setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timeoutId);
  }, [isCopied]);

  // Absent outside a secure context, so it can never be assumed to be there.
  const copy = () =>
    navigator.clipboard
      ?.writeText(value)
      .then(() => setIsCopied(true))
      .catch(() => setIsCopied(false));

  return (
    <button
      type="button"
      data-qa={dataQa}
      className={cx(classes.root, isCopied && classes.copied)}
      onClick={copy}>
      <TypographyBodySmall className={classes.value}>
        {value}
      </TypographyBodySmall>
      <div className={classes.action}>
        {isCopied ? <CheckIcon /> : <ContentCopyIcon />}
        <TypographyBodyMedium500>
          {isCopied ? copiedLabel : copyLabel}
        </TypographyBodyMedium500>
      </div>
    </button>
  );
}

export default CopyField;
